import { Injectable } from '@angular/core';
import { ChildActivationEnd, NavigationEnd, Router, } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  bookingSteps,
  bookingStepTitles,
  checkoutSteps,
  NavigationSteps,
} from 'src/app/shared/constants/bookingSteps.constant';
import spaceTypes from 'src/app/shared/constants/spaceTypes.constant';
import { UrlParametersService } from '../urlParameters/url-parameters.service';

export interface BookingStep {
  id: string;
  path: string[];
  index: number;
  title: string;
}

@Injectable({
  providedIn: 'root'
})
export class BookingNavigationService {
  private readonly currentStepSubject = new BehaviorSubject<BookingStep | null>(null);
  public currentStep$: Observable<BookingStep | null> = this.currentStepSubject.asObservable();

  private readonly steps: BookingStep[] = [
    {
      id: NavigationSteps.chooseRoom,
      path: [],
      index: 0,
      title: bookingStepTitles.chooseRoom
    },
    {
      id: NavigationSteps.roomServices,
      path: [bookingSteps.checkout, checkoutSteps.roomServices],
      index: 1,
      title: bookingStepTitles.roomServices
    },
    {
      id: NavigationSteps.checkout,
      path: [bookingSteps.checkout, checkoutSteps.orderRoom],
      index: 2,
      title: bookingStepTitles.checkout
    }
  ];

  private readonly spaceTypePaths = Object.values(spaceTypes).map((type) => `/${type.path}`);

  constructor(
    private router: Router,
    private parametersService: UrlParametersService
  ) {
    this.initializeStepTracking();
  }

  public getCurrentStep(): BookingStep | null {
    return this.currentStepSubject.value;
  }

  public getTotalSteps(): number {
    return this.steps.length;
  }

  public getCurrentStepNumber(): number {
    const current = this.getCurrentStep();
    return current ? current.index + 1 : 1;
  }

  public canGoBack(): boolean {
    const current = this.getCurrentStep();
    return current ? current.index > 0 : false;
  }

  public goBack(): void {
    const current = this.getCurrentStep();
    if (current && current.index > 0) {
      const previousStep = this.steps[current.index - 1];
      const path = previousStep.path ? previousStep.path : [];
      this.parametersService.navigate({}, path);
    }
  }

  public goToNextStep(): void {
    const current = this.getCurrentStep();
    if (current && current.index < this.steps.length - 1) {
      const nextStep = this.steps[current.index + 1];
      const path = nextStep.path ? nextStep.path : [];
      this.parametersService.navigate({}, path);
    }
  }

  public isBookingRoute(): boolean {
    const url = this.router.url;
    return this.spaceTypePaths.some((path) => url.includes(path));
  }

  public isPrevStep(step: BookingStep): boolean {
    const currentStep = this.getCurrentStep();
    return currentStep ? currentStep.index > step.index : false;
  }

  public isCurrentStep(step: BookingStep): boolean {
    const currentStep = this.getCurrentStep();
    return currentStep ? currentStep.id === step.id : false;
  }

  public isFutureStep(step: BookingStep): boolean {
    const currentStep = this.getCurrentStep();
    return currentStep ? currentStep.index < step.index : false;
  }

  public isStepDisabled(step: BookingStep): boolean {
    return !this.isPrevStep(step);
  }

  public navigateToStep(step: BookingStep): void {
    //TODO to be removed (old breadcrumb functionality)
    if (this.isStepDisabled(step)) {
      return;
    }
    const path = step.id ? [step.id] : [];
    this.parametersService.navigate({}, path);
  }

  public getSteps(): BookingStep[] {
    return this.steps;
  }

  private initializeStepTracking(): void {
    this.updateCurrentStep();
    this.router.events.subscribe((routerEvent) => {
      if (routerEvent instanceof NavigationEnd || routerEvent instanceof ChildActivationEnd) {
        this.updateCurrentStep();
      }
    });
  }

  private updateCurrentStep(): void {
    const url = this.router.url;
    let currentStep: BookingStep | null = null;

    if (!this.isBookingRoute()) {
      this.currentStepSubject.next(null);
      return;
    }

    currentStep =
      this.steps.find((step) => {
        if (!step.path || !step.path.length) return false;
        const joined = step.path.join('/');
        return url.includes(joined);
      }) ||
      this.steps[0] ||
      null;

    this.currentStepSubject.next(currentStep);
  }
}
